import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { appPath } from "@/lib/paths";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PlannerRecommendations } from "@/pages/ProductionPlanner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import {
  getCalendar,
  getPlans,
  createPlan,
  updatePlan,
  deletePlan,
  getReceptionPlans,
  createReceptionPlan,
  updateReceptionPlan,
  deleteReceptionPlan,
  getMilkBatches,
  getMilkBatch,
  getTemplates,
  getSuppliers,
  fmt,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProductTypes } from "@/context/ProductTypesContext";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Droplets,
  FlaskConical,
  CalendarClock,
  Truck,
  Play,
  Pencil,
  Trash2,
  ChevronDown,
} from "lucide-react";
import { useT } from "@/i18n/I18nContext";
import { formatDate } from "@/lib/dates";

// Local-date YYYY-MM-DD (avoid toISOString's UTC shift, which moves the day in +UTC zones).
const iso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const todayIso = iso(new Date());

const typeStyle = {
  intake: { cls: "bg-[hsl(var(--info)/0.12)] text-[hsl(var(--info))]", Icon: Droplets },
  production: { cls: "badge-ok", Icon: FlaskConical },
  plan: { cls: "badge-warn", Icon: CalendarClock },
  reception: { cls: "bg-[hsl(var(--accent)/0.18)] text-[hsl(var(--brand))]", Icon: Truck },
};

export default function Planning() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const { t, td } = useT();
  const { productTypes } = useProductTypes();
  const MONTHS = t("planning.months");
  const WEEKDAYS = t("planning.weekdays");
  const canPlan = can("admin", "manager", "qa");
  const [cursor, setCursor] = useState(() => new Date());
  const [tab, setTab] = useState("calendar");
  const [events, setEvents] = useState([]);
  const [plans, setPlans] = useState([]);
  const [receptions, setReceptions] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [recDialog, setRecDialog] = useState(null);
  const [intakeDetail, setIntakeDetail] = useState(null); // { loading, batch }

  const openIntake = async (id) => {
    setIntakeDetail({ loading: true, batch: null });
    try {
      const batch = await getMilkBatch(id);
      setIntakeDetail({ loading: false, batch });
    } catch {
      toast.error(t("planning.intakeNotFound"));
      setIntakeDetail(null);
    }
  };

  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  const load = async () => {
    const start = iso(new Date(year, month, 1));
    const end = iso(new Date(year, month + 1, 0));
    const [ev, pl, rp] = await Promise.all([getCalendar(start, end), getPlans(), getReceptionPlans()]);
    setEvents(ev);
    setPlans(pl.filter((p) => p.status === "planned"));
    setReceptions(rp.filter((r) => r.status === "scheduled"));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  const eventsByDay = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      (map[e.date] = map[e.date] || []).push(e);
    });
    return map;
  }, [events]);

  // Build calendar grid (Mon-first)
  const grid = useMemo(() => {
    const first = new Date(year, month, 1);
    const startOffset = (first.getDay() + 6) % 7; // Mon=0
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [year, month]);

  const startProduction = (plan) => navigate(appPath(`/productions?planId=${plan.id}`));
  const receiveMilk = (rp) => navigate(appPath(`/milk?receptionId=${rp.id}`));

  // drag holds { id, type }
  const [drag, setDrag] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);

  const onDropDay = async (dayIso) => {
    setDragOverDay(null);
    const d = drag;
    setDrag(null);
    if (!d) return;
    const ev = events.find((e) => e.type === d.type && e.id === d.id);
    if (ev && ev.date === dayIso) return; // same day
    try {
      if (d.type === "plan") await updatePlan(d.id, { planned_date: dayIso });
      else if (d.type === "reception") await updateReceptionPlan(d.id, { expected_date: dayIso });
      toast.success(t("planning.rescheduled", { date: dayIso }));
      load();
    } catch {
      toast.error(t("planning.failedReschedule"));
    }
  };

  const removePlan = async (id) => {
    await deletePlan(id);
    toast.success(t("planning.planRemoved"));
    load();
  };

  const removeReception = async (id) => {
    await deleteReceptionPlan(id);
    toast.success(t("planning.receptionRemoved"));
    load();
  };

  // Renders one calendar event chip. `inPopover` disables drag/truncation for the overflow dropdown.
  const renderEvent = (e, inPopover = false) => {
    const st = typeStyle[e.type];
    const Icon = st.Icon;
    const draggable = !inPopover && (e.type === "plan" || e.type === "reception") && canPlan;
    const isProduction = e.type === "production";
    const isIntake = e.type === "intake";
    return (
      <div
        key={`${inPopover ? "pop-" : ""}${e.type}-${e.id}`}
        draggable={draggable}
        onDragStart={(ev) => { if (draggable) { setDrag({ id: e.id, type: e.type }); ev.stopPropagation(); } }}
        onDragEnd={() => { setDrag(null); setDragOverDay(null); }}
        onClick={(ev) => {
          if (isProduction) { ev.stopPropagation(); navigate(appPath(`/productions?openId=${e.id}`)); }
          else if (isIntake) { ev.stopPropagation(); openIntake(e.id); }
          else { ev.stopPropagation(); }
        }}
        className={`flex items-center gap-1 ${inPopover ? "" : "truncate"} rounded px-1 py-0.5 text-[10px] ${st.cls} ${draggable ? "cursor-grab active:cursor-grabbing" : ""} ${(isProduction || isIntake) ? "cursor-pointer hover:brightness-95" : ""}`}
        title={`${e.title} ${e.subtitle}${draggable ? t("planning.dragToReschedule") : ""}${isProduction ? t("planning.clickOpenQc") : ""}${isIntake ? t("planning.clickSeeMilk") : ""}`}
        data-testid={(e.type === "plan" || e.type === "reception" || isProduction || isIntake) ? `cal-event-${e.type}-${e.id}${inPopover ? "-pop" : ""}` : undefined}
      >
        <Icon className="h-2.5 w-2.5 shrink-0" />
        <span className={inPopover ? "" : "truncate"}>{e.title.replace(/^(Milk in:|Produced:|Plan:|Milk reception:) /, "")}</span>
      </div>
    );
  };

  return (
    <Layout
      actions={
        tab === "calendar" && canPlan ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" data-testid="new-plan-menu-button">
                <Plus className="mr-1.5 h-4 w-4" /> {t("planning.new")} <ChevronDown className="ml-1 h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setDialog({ planned_date: todayIso, product_type: productTypes[0] })} data-testid="new-plan-button">
                <FlaskConical className="mr-2 h-4 w-4" /> {t("planning.productionPlan")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setRecDialog({ expected_date: todayIso })} data-testid="new-reception-button">
                <Truck className="mr-2 h-4 w-4" /> {t("planning.milkReception")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null
      }
    >
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-5">
          <TabsTrigger value="calendar" data-testid="planning-tab-calendar">{t("planning.tabCalendar")}</TabsTrigger>
          <TabsTrigger value="recommendations" data-testid="planning-tab-recommendations">{t("planning.tabRecommendations")}</TabsTrigger>
        </TabsList>

        <TabsContent value="calendar">
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* Calendar */}
        <Card className="xl:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">{MONTHS[month]} {year}</CardTitle>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="cal-today">{t("common.today")}</Button>
              <Button variant="outline" size="icon" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex flex-wrap gap-3 text-xs">
              <Legend cls={typeStyle.intake.cls} Icon={Droplets} label={t("planning.legMilkIn")} />
              <Legend cls={typeStyle.production.cls} Icon={FlaskConical} label={t("planning.legProduced")} />
              <Legend cls={typeStyle.plan.cls} Icon={CalendarClock} label={t("planning.legPlanned")} />
              <Legend cls={typeStyle.reception.cls} Icon={Truck} label={t("planning.milkReception")} />
            </div>
            {canPlan && <p className="mb-2 text-[11px] text-muted-foreground">{t("planning.tipDrag")}</p>}
            <p className="mb-2 text-[11px] text-muted-foreground">{t("planning.tipClick")}</p>
            <div className="grid grid-cols-7 gap-px overflow-hidden rounded-lg border border-border bg-border">
              {WEEKDAYS.map((w) => (
                <div key={w} className="bg-secondary px-2 py-1.5 text-center text-[11px] font-medium text-muted-foreground">{w}</div>
              ))}
              {grid.map((date, i) => {
                const key = date ? iso(date) : `e${i}`;
                const dayEvents = date ? eventsByDay[key] || [] : [];
                const isToday = key === todayIso;
                return (
                  <div
                    key={key}
                    className={`min-h-[92px] bg-card p-1.5 ${date ? "cursor-pointer hover:bg-secondary/50" : "bg-background/40"} ${dragOverDay === key ? "ring-2 ring-inset ring-[hsl(var(--brand))] bg-[hsl(var(--brand)/0.06)]" : ""} transition-colors`}
                    onClick={() => date && canPlan && setDialog({ planned_date: key, product_type: productTypes[0] })}
                    onDragOver={(e) => { if (date && canPlan && drag) { e.preventDefault(); setDragOverDay(key); } }}
                    onDragLeave={() => { if (dragOverDay === key) setDragOverDay(null); }}
                    onDrop={(e) => { e.preventDefault(); if (date && canPlan) onDropDay(key); }}
                    data-testid={date ? `cal-day-${key}` : undefined}
                  >
                    {date && (
                      <>
                        <div className={`mb-1 text-right text-xs ${isToday ? "font-bold text-[hsl(var(--brand))]" : "text-muted-foreground"}`}>
                          {isToday ? <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(var(--brand))] text-white">{date.getDate()}</span> : date.getDate()}
                        </div>
                        <div className="space-y-1">
                          {dayEvents.slice(0, 3).map((e) => renderEvent(e, false))}
                          {dayEvents.length > 3 && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  onClick={(ev) => ev.stopPropagation()}
                                  className="w-full rounded px-1 py-0.5 text-left text-[10px] font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                                  data-testid={`cal-more-${key}`}
                                >
                                  {t("planning.moreEvents", { n: dayEvents.length - 3 })}
                                </button>
                              </PopoverTrigger>
                              <PopoverContent
                                align="start"
                                className="w-72 p-2"
                                onClick={(ev) => ev.stopPropagation()}
                                data-testid={`cal-more-content-${key}`}
                              >
                                <div className="mb-1.5 flex items-center justify-between px-1">
                                  <span className="text-xs font-semibold">{key}</span>
                                  <span className="text-[11px] text-muted-foreground">{t("planning.itemsCount", { n: dayEvents.length })}</span>
                                </div>
                                <div className="thin-scroll max-h-72 space-y-1 overflow-y-auto">
                                  {dayEvents.map((e) => renderEvent(e, true))}
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Right column */}
        <div className="space-y-6">
        {/* To-do plans */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarClock className="h-4 w-4 text-[hsl(var(--warning))]" /> {t("planning.todo")} ({plans.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {plans.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">{t("planning.noPlanned")}</p>}
            {plans.map((p) => (
              <div key={p.id} className="rounded-lg border border-border p-3" data-testid={`plan-card-${p.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium">{td(p.product_type)}</div>
                    <div className="text-xs text-muted-foreground">{p.planned_date} · {p.planned_volume_l ? `${fmt(p.planned_volume_l)} L` : t("planning.volumeTbd")}</div>
                  </div>
                  <Badge className="badge-warn">{p.operator_name || t("planning.unassigned")}</Badge>
                </div>
                {p.target_fat != null && <div className="mt-1 font-mono text-xs text-muted-foreground">{t("planning.targetFatLine", { n: p.target_fat })}</div>}
                {p.notes && <div className="mt-1 text-xs text-muted-foreground">{p.notes}</div>}
                <div className="mt-2 flex items-center gap-1">
                  <Button size="sm" className="flex-1" onClick={() => startProduction(p)} data-testid={`plan-start-${p.id}`}>
                    <Play className="mr-1.5 h-3.5 w-3.5" /> {t("planning.startProduction")}
                  </Button>
                  {canPlan && (
                    <>
                      <Button variant="ghost" size="icon" onClick={() => setDialog({ ...p })} data-testid={`plan-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => removePlan(p.id)} data-testid={`plan-delete-${p.id}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Incoming milk receptions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Truck className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("planning.incomingMilk")} ({receptions.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {receptions.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">{t("planning.noReceptions")}</p>}
            {receptions.map((r) => (
              <div key={r.id} className="rounded-lg border border-border p-3" data-testid={`reception-card-${r.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium">{r.supplier}</div>
                    <div className="text-xs text-muted-foreground">{r.expected_date} · {r.expected_volume_l ? t("planning.volExpected", { vol: fmt(r.expected_volume_l) }) : t("planning.volumeTbd")}</div>
                  </div>
                  {r.expected_fat != null && <Badge className="badge-warn">{t("planning.fatLine", { n: r.expected_fat })}</Badge>}
                </div>
                {r.notes && <div className="mt-1 text-xs text-muted-foreground">{r.notes}</div>}
                <div className="mt-2 flex items-center gap-1">
                  <Button size="sm" className="flex-1" onClick={() => receiveMilk(r)} data-testid={`reception-receive-${r.id}`}>
                    <Droplets className="mr-1.5 h-3.5 w-3.5" /> {t("planning.receiveMilk")}
                  </Button>
                  {canPlan && (
                    <>
                      <Button variant="ghost" size="icon" onClick={() => setRecDialog({ ...r })} data-testid={`reception-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => removeReception(r.id)} data-testid={`reception-delete-${r.id}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
        </div>
      </div>
        </TabsContent>

        <TabsContent value="recommendations">
          <PlannerRecommendations onPlanned={() => { load(); setTab("calendar"); }} />
        </TabsContent>
      </Tabs>

      <PlanDialog dialog={dialog} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />
      <ReceptionDialog dialog={recDialog} onClose={() => setRecDialog(null)} onSaved={() => { setRecDialog(null); load(); }} />
      <IntakeDetailDialog detail={intakeDetail} onClose={() => setIntakeDetail(null)} />
    </Layout>
  );
}

function IntakeDetailDialog({ detail, onClose }) {
  const { t } = useT();
  const b = detail?.batch;
  const taken = b ? Number(b.quantity_l || 0) : 0;
  const remaining = b ? Number(b.remaining_l ?? 0) : 0;
  const used = Math.max(taken - remaining, 0);
  const usedPct = taken > 0 ? Math.round((used / taken) * 100) : 0;
  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="intake-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Droplets className="h-4 w-4 text-[hsl(var(--info))]" /> {t("planning.milkReception")}
          </DialogTitle>
        </DialogHeader>
        {detail?.loading || !b ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t("common.loading")}</p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium" data-testid="intake-supplier">{b.supplier}</div>
                <div className="font-mono text-xs text-muted-foreground">{b.batch_code}</div>
              </div>
              <Badge variant="secondary">{formatDate(b.date)}</Badge>
            </div>

            <div className="rounded-lg border border-border p-3">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{t("planning.taken")}</div>
                  <div className="font-mono text-lg font-semibold" data-testid="intake-taken">{fmt(taken)} L</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{t("planning.used")}</div>
                  <div className="font-mono text-lg font-semibold text-[hsl(var(--brand))]" data-testid="intake-used">{fmt(used)} L</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{t("common.remaining")}</div>
                  <div className="font-mono text-lg font-semibold" data-testid="intake-remaining">{fmt(remaining)} L</div>
                </div>
              </div>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-secondary">
                <div className="h-full rounded-full bg-[hsl(var(--brand))]" style={{ width: `${usedPct}%` }} />
              </div>
              <div className="mt-1 text-center text-[11px] text-muted-foreground">{t("planning.pctUsed", { n: usedPct })}</div>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center text-sm">
              <div className="rounded-md border border-border py-2">
                <div className="text-[11px] text-muted-foreground">{t("dashboard.fat")}</div>
                <div className="font-mono font-medium" data-testid="intake-fat">{b.fat != null ? `${fmt(b.fat, 2)}%` : "–"}</div>
              </div>
              <div className="rounded-md border border-border py-2">
                <div className="text-[11px] text-muted-foreground">{t("planning.protein")}</div>
                <div className="font-mono font-medium">{b.protein != null ? `${fmt(b.protein, 2)}%` : "–"}</div>
              </div>
              <div className="rounded-md border border-border py-2">
                <div className="text-[11px] text-muted-foreground">pH</div>
                <div className="font-mono font-medium" data-testid="intake-ph">{b.ph != null ? fmt(b.ph, 2) : "–"}</div>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="intake-detail-close">{t("common.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Legend = ({ cls, Icon, label }) => (
  <span className="flex items-center gap-1.5">
    <span className={`flex h-4 w-4 items-center justify-center rounded ${cls}`}><Icon className="h-2.5 w-2.5" /></span>
    {label}
  </span>
);

function PlanDialog({ dialog, onClose, onSaved }) {
  const { t, td } = useT();
  const { productTypes } = useProductTypes();
  const [f, setF] = useState(dialog);
  const [batches, setBatches] = useState([]);
  const [templates, setTemplates] = useState([]);
  useEffect(() => setF(dialog), [dialog]);
  useEffect(() => {
    if (dialog) {
      getMilkBatches(true).then(setBatches);
    }
  }, [dialog]);
  useEffect(() => {
    if (f?.product_type) getTemplates(f.product_type).then(setTemplates);
  }, [f?.product_type]);
  if (!f) return null;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const num = (v) => (v === "" || v == null ? null : Number(v));

  const save = async () => {
    if (!f.product_type || !f.planned_date) {
      toast.error(t("planning.productDateRequired"));
      return;
    }
    const payload = {
      product_type: f.product_type,
      planned_date: f.planned_date,
      planned_volume_l: num(f.planned_volume_l),
      target_fat: num(f.target_fat),
      template_id: f.template_id || null,
      milk_batch_id: f.milk_batch_id && f.milk_batch_id !== "none" ? f.milk_batch_id : null,
      operator_name: f.operator_name || "",
      notes: f.notes || "",
    };
    try {
      if (f.id) await updatePlan(f.id, payload);
      else await createPlan(payload);
      toast.success(t("planning.planSaved"));
      onSaved();
    } catch {
      toast.error(t("planning.failedSavePlan"));
    }
  };

  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{f.id ? t("planning.editPlan") : t("planning.newProductionPlan")}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>{t("common.product")}</Label>
            <Select value={f.product_type} onValueChange={(v) => set("product_type", v)}>
              <SelectTrigger data-testid="plan-product-select"><SelectValue /></SelectTrigger>
              <SelectContent>{productTypes.map((p) => <SelectItem key={p} value={p}>{td(p)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t("planning.plannedDate")}</Label>
            <Input type="date" value={f.planned_date || ""} onChange={(e) => set("planned_date", e.target.value)} data-testid="plan-date-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("planning.plannedVolume")}</Label>
            <Input type="number" value={f.planned_volume_l ?? ""} onChange={(e) => set("planned_volume_l", e.target.value)} className="text-right font-mono" data-testid="plan-volume-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("prod.targetFat")}</Label>
            <Input type="number" step="0.01" value={f.target_fat ?? ""} onChange={(e) => set("target_fat", e.target.value)} className="text-right font-mono" data-testid="plan-fat-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("planning.milkBatch")}</Label>
            <Select value={f.milk_batch_id || "none"} onValueChange={(v) => set("milk_batch_id", v)}>
              <SelectTrigger data-testid="plan-milk-select"><SelectValue placeholder={t("common.none")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("common.none")}</SelectItem>
                {batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.batch_code} · {fmt(b.remaining_l)}L</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t("prod.operator")}</Label>
            <Input value={f.operator_name || ""} onChange={(e) => set("operator_name", e.target.value)} data-testid="plan-operator-input" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t("common.notes")}</Label>
            <Textarea rows={2} value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="plan-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={save} data-testid="plan-save-button">{t("planning.savePlan")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ReceptionDialog({ dialog, onClose, onSaved }) {
  const { t } = useT();
  const [f, setF] = useState(dialog);
  const [suppliers, setSuppliers] = useState([]);
  useEffect(() => setF(dialog), [dialog]);
  useEffect(() => {
    // Milk suppliers list for the dropdown (active only).
    getSuppliers({ category: "milk" })
      .then((list) => setSuppliers((list || []).filter((s) => s.active !== false)))
      .catch(() => {});
  }, []);
  if (!f) return null;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const num = (v) => (v === "" || v == null ? null : Number(v));
  // Keep an old plan's supplier selectable even if it's no longer in the suppliers list.
  const supplierNames = suppliers.map((s) => s.name);
  const options = f.supplier && !supplierNames.includes(f.supplier)
    ? [{ id: "__current", name: f.supplier }, ...suppliers]
    : suppliers;

  const save = async () => {
    if (!f.supplier || !f.expected_date) {
      toast.error(t("planning.supplierDateRequired"));
      return;
    }
    const payload = {
      supplier: f.supplier,
      expected_date: f.expected_date,
      expected_volume_l: num(f.expected_volume_l),
      expected_fat: num(f.expected_fat),
      notes: f.notes || "",
    };
    try {
      if (f.id) await updateReceptionPlan(f.id, payload);
      else await createReceptionPlan(payload);
      toast.success(t("planning.receptionSaved"));
      onSaved();
    } catch {
      toast.error(t("planning.failedSaveReception"));
    }
  };

  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{f.id ? t("planning.editReception") : t("planning.scheduleReception")}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t("planning.supplier")}</Label>
            <Select value={f.supplier || ""} onValueChange={(v) => set("supplier", v)}>
              <SelectTrigger data-testid="reception-supplier-select">
                <SelectValue placeholder={t("planning.supplierPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {options.length === 0 && (
                  <div className="px-3 py-2 text-xs text-muted-foreground">{t("planning.noSuppliers")}</div>
                )}
                {options.map((s) => (
                  <SelectItem key={s.id} value={s.name} data-testid={`reception-supplier-option-${s.id}`}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t("planning.expectedDate")}</Label>
            <Input type="date" value={f.expected_date || ""} onChange={(e) => set("expected_date", e.target.value)} data-testid="reception-date-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("planning.expectedVolume")}</Label>
            <Input type="number" value={f.expected_volume_l ?? ""} onChange={(e) => set("expected_volume_l", e.target.value)} className="text-right font-mono" data-testid="reception-volume-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("planning.expectedFat")}</Label>
            <Input type="number" step="0.01" value={f.expected_fat ?? ""} onChange={(e) => set("expected_fat", e.target.value)} className="text-right font-mono" data-testid="reception-fat-input" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t("common.notes")}</Label>
            <Textarea rows={2} value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="reception-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={save} data-testid="reception-save-button">{t("planning.saveReception")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
